import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { recallReportService } from '@/services';
import { queryKeys } from '@/services/query-keys';

interface UseRecallReportsOptions {
  counterparty?: string;
  refetchInterval?: number | false;
}

export const useRecallReports = (options?: UseRecallReportsOptions) => {
  const { counterparty, refetchInterval } = options ?? {};
  const filters = counterparty ? { counterparty } : undefined;

  const query = useInfiniteQuery({
    queryKey: queryKeys.recallReport.list(filters),
    queryFn: ({ pageParam }) =>
      recallReportService.getRecallReports({ ...filters, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? (lastPageParam as number) + lastPage.results.length : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval,
  });

  // Auto-fetch remaining pages in background (capped to avoid runaway requests)
  const MAX_PAGES = 10;
  useEffect(() => {
    const pageCount = query.data?.pages.length ?? 0;
    if (query.hasNextPage && !query.isFetchingNextPage && pageCount < MAX_PAGES) {
      query.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: trigger only on the specific query state fields; the full `query` object is recreated by react-query on every render
  }, [query.hasNextPage, query.isFetchingNextPage, query.data]);

  const data = useMemo(
    () => query.data?.pages.flatMap((p) => p.results) ?? [],
    [query.data],
  );

  return { ...query, data };
};
